"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, CircleDot, ListOrdered, XCircle } from "lucide-react";
import { cn, scrollToAnchor } from "@/lib/utils";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  MobileBottomToolRegistration,
  useMobileBottomTools,
} from "@/components/mobile-bottom-tools";
import { useScrollSpy } from "@/hooks/useScrollSpy";
import { useExamScore, type ProblemScoreStatus } from "@/components/ExamScoreContext";

/**
 * 문제 목록 네비 (JCG-2066).
 *
 * 퀴즈/기출 페이지는 본문이 문제뿐이라 heading 기반 목차가 무의미하다. 대신 "N번 + 문제 일부"
 * 카드를 세로로 쌓아, 누르면 해당 문제로 스크롤되는 네비를 좌측 사이드바 목차 자리에 둔다.
 * RandomizedProblems 가 각 문제를 `#quiz-problem-N` 앵커로 감싸고, 문제 미리보기 텍스트 배열을
 * problems prop 으로 내려준다. 이 컴포넌트는 카드를 problemNavSlot 에 createPortal 하고,
 * 현재 보고 있는 문제는 IntersectionObserver 로 하이라이트한다.
 *
 * 셸(데스크탑 글 페이지) 안에서는 좌측 슬롯에 portal.
 * 비셸·모바일에서는 공통 하단 dock의 문제목록 패널로 등록한다.
 */
interface ProblemListNavProps {
  /** index = 문제 번호 - 1. 데이터 로딩 전엔 preview가 undefined. */
  problems: Array<{
    id?: string;
    preview?: string;
  }>;
}

const ANCHOR_PREFIX = "quiz-problem-";
const MOBILE_TOOL_ID = "problem-list";

/** 문제 번호(1-base) → RandomizedProblems 가 붙인 앵커 id. */
const anchorIdFor = (n: number) => `${ANCHOR_PREFIX}${n}`;

const STATUS_STYLES: Record<ProblemScoreStatus, {
  label: string;
  button: string;
  number: string;
  icon: string;
}> = {
  correct: {
    label: "정답",
    button: "border-green-200 bg-green-50/70 hover:border-green-300 dark:border-green-900/70 dark:bg-green-950/20 dark:hover:border-green-700",
    number: "bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300",
    icon: "text-green-600 dark:text-green-400",
  },
  incorrect: {
    label: "오답",
    button: "border-red-200 bg-red-50/70 hover:border-red-300 dark:border-red-900/70 dark:bg-red-950/20 dark:hover:border-red-700",
    number: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300",
    icon: "text-red-600 dark:text-red-400",
  },
  partial: {
    label: "부분 정답",
    button: "border-blue-200 bg-blue-50/70 hover:border-blue-300 dark:border-blue-900/70 dark:bg-blue-950/20 dark:hover:border-blue-700",
    number: "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
    icon: "text-blue-600 dark:text-blue-400",
  },
};

/** 마크다운/HTML 마커를 걷어내 1줄 미리보기용 평문으로 정리. */
function toPreview(md: string | undefined): string {
  if (!md) return "";
  return md
    .replace(/```[\s\S]*?```/g, " ") // 코드 펜스 제거
    .replace(/`([^`]*)`/g, "$1") // 인라인 코드
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // 이미지
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // 링크 → 텍스트
    .replace(/<[^>]+>/g, " ") // HTML 태그
    .replace(/[#>*_~`|]/g, " ") // 마크다운 마커
    .replace(/\s+/g, " ") // 공백 정리
    .trim();
}

/** 문제 번호를 받아 이동을 수행하는 콜백. 셸/모바일이 서로 다른 방식을 주입한다. */
type SelectProblem = (n: number) => void;

/**
 * 하단 시트용 등록 컴포넌트 (JCG-2987/JCG-2988).
 *
 * `useMobileBottomTools` 는 provider 밖에서 호출하면 throw 한다. 데스크탑 셸 페이지
 * (`/mock-exam`·`/final-mock-exam`)는 PostLayout 을 거치지 않아 provider 가 없으므로,
 * 컨텍스트 접근을 이 자식 컴포넌트에 가둬 `!inShell` 일 때만 렌더한다
 * (ExamScoreModal·RandomProblems 가 쓰는 것과 같은 패턴).
 *
 * 이동 타이밍: 시트가 열려 있는 동안에는 `useBodyScrollLock` 이 body 를 `position: fixed` 로
 * 잠가 `scrollIntoView` 가 통째로 무시되고, 락 해제 시 원래 스크롤 위치까지 복원한다.
 * 그래서 누른 앵커를 state 에 담아두고 시트를 닫은 뒤, `openToolId` 가 바뀐 커밋의 effect 에서
 * 이동한다. React 는 한 커밋의 cleanup(= 락 해제·위치 복원)을 모두 돌린 뒤 effect 본문을
 * 실행하므로 순서가 보장된다.
 */
const MobileProblemNavRegistration: React.FC<{
  /** onSelect 를 주입받아 카드 목록을 그리는 렌더 함수. 부모가 참조를 안정시켜 넘긴다. */
  renderList: (onSelect: SelectProblem) => React.ReactNode;
}> = ({ renderList }) => {
  const { closeSheet, openToolId } = useMobileBottomTools();
  // 누를 때마다 새 객체를 만들어, 처리 여부를 객체 동일성으로 판별한다(같은 문제 재선택도 구분).
  const [pending, setPending] = useState<{ anchorId: string } | null>(null);
  const handledRef = useRef<{ anchorId: string } | null>(null);

  const handleSelect = useCallback<SelectProblem>(
    (n) => {
      setPending({ anchorId: anchorIdFor(n) });
      closeSheet();
    },
    [closeSheet],
  );

  useEffect(() => {
    if (!pending) return;
    if (openToolId === MOBILE_TOOL_ID) return; // 아직 시트가 열려 있으면 대기
    if (handledRef.current === pending) return; // 시트를 다시 열고 닫아도 재이동하지 않는다
    handledRef.current = pending;
    scrollToAnchor(pending.anchorId);
  }, [openToolId, pending]);

  const content = useMemo(
    () => renderList(handleSelect),
    [renderList, handleSelect],
  );

  // tool 객체가 매 렌더 새로 생기면 updateTool → 컨텍스트 변경 → 리렌더 무한 루프가 된다.
  // renderList 는 컨텍스트를 구독하지 않는 부모가 주므로, 여기서 memo 하면 참조가 고정된다.
  const tool = useMemo(
    () =>
      ({
        id: MOBILE_TOOL_ID,
        kind: "panel",
        label: "문제목록",
        icon: ListOrdered,
        order: 5,
        ariaLabel: "문제 목록 패널 열기",
        sheet: {
          title: "문제 목록",
          resizable: true,
          defaultHeightRatio: 0.38,
          minHeightRatio: 0.3,
          maxHeightRatio: 0.85,
          content,
        },
      }) as const,
    [content],
  );

  return <MobileBottomToolRegistration tool={tool} />;
};

MobileProblemNavRegistration.displayName = "MobileProblemNavRegistration";

const ProblemListNav: React.FC<ProblemListNavProps> = ({ problems }) => {
  const { inShell, problemNavSlot } = useArticleShellSlots();
  const examScore = useExamScore();
  const total = problems.length;

  // 현재 뷰포트 상단에 가장 가까운 문제를 active 로 추적 (공용 scroll-spy 훅, JCG-2102).
  const anchorIds = useMemo(
    () => Array.from({ length: total }, (_, i) => anchorIdFor(i + 1)),
    [total],
  );
  const activeId = useScrollSpy(anchorIds, { enabled: total > 0 });
  // 스크롤 전(activeId=null)에는 1번을 기본 활성으로 표시.
  const active = activeId ? Number(activeId.slice(ANCHOR_PREFIX.length)) : 1;

  // 채점 상태는 ExamScoreProvider 안(=이 위치)에서만 읽을 수 있다. 하단 시트는 provider 바깥에
  // 렌더되므로 목록 JSX 를 여기서 만들어 넘긴다. onSelect 만 호출부가 주입한다.
  const renderList = useCallback(
    (onSelect: SelectProblem) => (
      <div className="flex flex-col gap-1.5 pt-1">
        {problems.map((item, i) => {
          const n = i + 1;
          const isActive = active === n;
          const preview = toPreview(item.preview);
          const status =
            (item.id ? examScore?.problemResultsById[item.id] : undefined) ??
            examScore?.problemResults[n];
          const statusStyle = status ? STATUS_STYLES[status] : null;
          const StatusIcon =
            status === "correct"
              ? CheckCircle2
              : status === "partial"
                ? CircleDot
                : XCircle;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onSelect(n)}
              aria-label={`${n}번 문제로 이동${statusStyle ? `, ${statusStyle.label}` : ""}`}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                statusStyle
                  ? statusStyle.button
                  : isActive
                    ? "border-amber-400 bg-amber-50 dark:border-amber-500/60 dark:bg-amber-950/40"
                    : "border-border bg-card hover:border-amber-300 dark:hover:border-amber-700",
                isActive &&
                  statusStyle &&
                  "ring-2 ring-amber-400/70 ring-offset-1 ring-offset-background dark:ring-amber-500/70",
              )}
            >
              <span
                className={cn(
                  "flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-md px-1 text-[11px] font-bold tabular-nums",
                  statusStyle
                    ? statusStyle.number
                    : isActive
                      ? "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                      : "bg-muted text-muted-foreground",
                )}
              >
                {n}
              </span>
              <span
                className={cn(
                  "line-clamp-1 min-w-0 flex-1 text-[12.5px] leading-snug",
                  isActive && !statusStyle
                    ? "font-semibold text-amber-800 dark:text-amber-200"
                    : "text-foreground/80",
                )}
              >
                {preview || `${n}번 문제`}
              </span>
              {statusStyle && (
                <StatusIcon
                  className={cn("h-4 w-4 shrink-0", statusStyle.icon)}
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>
    ),
    [problems, examScore, active],
  );

  if (total === 0) return null;

  // 셸 좌측 네비는 시트를 쓰지 않아 스크롤 락도 없다. 바로 이동한다.
  if (inShell) {
    return problemNavSlot
      ? createPortal(
          renderList((n) => scrollToAnchor(anchorIdFor(n))),
          problemNavSlot,
        )
      : null;
  }

  return <MobileProblemNavRegistration renderList={renderList} />;
};

ProblemListNav.displayName = "ProblemListNav";

export default ProblemListNav;
